import { ChevronRight, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import {
  evaluateAgentConfigWarnings,
  warningsForKey,
  type AgentConfigKeyWarning,
  type AgentConfigValueSnapshot,
} from "@prospect/shared";

import { useAgentConfig, useUpdateAgentConfig } from "@/api/agent-config";
import { useModels } from "@/api/models";
import type {
  AgentConfigInputKind,
  AgentConfigResponseSection,
  AgentConfigRow,
} from "@/api/types";
import { ErrorBanner } from "@/components/ErrorBanner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  dedupeEmails,
  isValidEmail,
  parseEmailList,
  serializeEmailList,
  splitEmailString,
} from "@/lib/parse-emails";
import { cn } from "@/lib/utils";

/**
 * `/settings` — Phase 2 (#75) editable browser for the `agent_config`
 * Sheet, grouped into operator-facing sections.
 *
 * Phase 2 adds inline edit + write-back to the live Sheet via the
 * orchestrator's `PUT /api/agent-config/:key` route. Every known row
 * gets an editor; unknown ("Other") rows stay read-only because the
 * route refuses to write keys absent from `AGENT_CONFIG_DEFAULTS`.
 *
 * Save UX is per-page: a sticky header strip surfaces a single Save
 * button counting the dirty rows, and clicking it fires every dirty
 * row's PUT through `Promise.allSettled` so one row's failure does
 * not block the others. Errored rows stay dirty so the operator can
 * retry without losing input; succeeded rows clear their dirty marker
 * and the GET cache is invalidated, so the next render reflects the
 * Sheet's new state.
 *
 * Cross-field warning chips are warn-and-allow: a chip on a row never
 * blocks save, and the agent-side Zod schemas at run start are the
 * loud-fail backstop. The single rule today is
 * `validator_max_rows_per_run < max_new_drafts_per_run`; new rules go
 * in `packages/shared/src/agent-config-warnings.ts`.
 *
 * Each section card collapses on click via the native
 * `<details>`/`<summary>` element — keyboard, ARIA, and focus
 * management for free, no extra dependency. State is intentionally
 * not persisted across reloads at M1: every visit lands on the
 * fully-expanded view.
 */

interface Section {
  id: AgentConfigResponseSection;
  title: string;
  description: string;
}

const NAMED_SECTIONS: Section[] = [
  {
    id: "curator",
    title: "Curator",
    description: "Catalogue tuning and quality knobs the curator reads at run start.",
  },
  {
    id: "validator",
    title: "Validator",
    description: "Validator-specific knobs (model, web tools, run caps, quality threshold).",
  },
  {
    id: "catalogue",
    title: "Catalogue",
    description:
      "Knobs acting on the published tools_and_services catalogue rather than on incoming drafts — drives the per-row freshness badges on /catalogue.",
  },
  {
    id: "runtime",
    title: "Runtime",
    description:
      "Curator runtime knobs (model, web tools) plus the cron schedule honoured once the M3 scheduler ships.",
  },
  {
    id: "cost",
    title: "Cost",
    description: "Per-run LLM spend threshold above which the orchestrator emails a cost alert.",
  },
  {
    id: "notifications",
    title: "Notifications",
    description: "Recipients and HITL deadlines.",
  },
  {
    id: "public-chat",
    title: "Public chat",
    description:
      "Abuse / cost defence for the M2 chatbot surface — per-session prompt cap, per-prompt length cap, and the per-IP request rate. Read live by the orchestrator preHandler.",
  },
];

const OTHER_SECTION: Section = {
  id: "other",
  title: "Other",
  description:
    "Keys present in the live Sheet but not in AGENT_CONFIG_DEFAULTS. Either a typo, a deprecated knob, or a key added directly without code support yet. Read-only — edits to these still go through the live Sheet directly.",
};

type DirtyMap = ReadonlyMap<string, string | number>;
type SaveStatus = "idle" | "saving" | "saved" | "failed";
type SaveStatusMap = ReadonlyMap<string, { status: SaveStatus; message?: string }>;

export function Settings() {
  const query = useAgentConfig();
  const update = useUpdateAgentConfig();
  const [dirty, setDirty] = useState<DirtyMap>(new Map());
  const [saveStatus, setSaveStatus] = useState<SaveStatusMap>(new Map());

  const setRowValue = useCallback((key: string, value: string | number) => {
    setDirty((prev) => {
      const next = new Map(prev);
      next.set(key, value);
      return next;
    });
  }, []);

  const clearRow = useCallback((key: string) => {
    setDirty((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const groups = useMemo(() => {
    if (!query.data) return null;
    const byId = new Map<AgentConfigResponseSection, AgentConfigRow[]>();
    for (const row of query.data.rows) {
      const list = byId.get(row.section) ?? [];
      list.push(row);
      byId.set(row.section, list);
    }
    const sections = [...NAMED_SECTIONS];
    if ((byId.get("other")?.length ?? 0) > 0) sections.push(OTHER_SECTION);
    return sections.map((section) => ({
      section,
      rows: byId.get(section.id) ?? [],
    }));
  }, [query.data]);

  // Effective snapshot used by the cross-field warning rules: live
  // values from the Sheet, overridden by any pending dirty edits so
  // chips react instantly as the operator types.
  const snapshot = useMemo<AgentConfigValueSnapshot>(() => {
    const out: Record<string, string | number | null | undefined> = {};
    if (query.data) {
      for (const row of query.data.rows) out[row.key] = row.value;
    }
    for (const [key, value] of dirty) out[key] = value;
    return out;
  }, [query.data, dirty]);

  const firedWarnings = useMemo(
    () => evaluateAgentConfigWarnings(snapshot),
    [snapshot],
  );

  const onSave = useCallback(async () => {
    if (dirty.size === 0) return;
    const entries = [...dirty.entries()];
    setSaveStatus((prev) => {
      const next = new Map(prev);
      for (const [key] of entries) next.set(key, { status: "saving" });
      return next;
    });
    const results = await Promise.allSettled(
      entries.map(([key, value]) =>
        update.mutateAsync({ key, value }).then(() => key),
      ),
    );
    setSaveStatus((prev) => {
      const next = new Map(prev);
      for (let i = 0; i < results.length; i++) {
        const [key] = entries[i]!;
        const result = results[i]!;
        if (result.status === "fulfilled") {
          next.set(key, { status: "saved" });
        } else {
          const message =
            result.reason instanceof Error
              ? result.reason.message
              : "save failed";
          next.set(key, { status: "failed", message });
        }
      }
      return next;
    });
    // Only clear dirty entries whose snapshot value still matches what
    // we just sent. If the operator edited a row mid-save, their new
    // value is now in `prev` and must be preserved.
    setDirty((prev) => {
      const next = new Map(prev);
      for (let i = 0; i < results.length; i++) {
        const [key, sentValue] = entries[i]!;
        if (results[i]!.status !== "fulfilled") continue;
        if (next.get(key) === sentValue) next.delete(key);
      }
      return next;
    });
  }, [dirty, update]);

  // `update.isPending` only tracks the latest mutation in a fan-out, so
  // on a multi-row Save it can flip back to false while earlier rows
  // are still in flight. Derive the gate from the per-row saveStatus
  // map instead — every entry pinned to "saving" until its row settles.
  const isSaving = useMemo(() => {
    for (const status of saveStatus.values()) {
      if (status.status === "saving") return true;
    }
    return false;
  }, [saveStatus]);

  if (query.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />;
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <p className="font-mono text-xs uppercase tracking-label text-text-muted">
          Settings
        </p>
        <h1 className="font-display text-3xl text-text">Agent configuration</h1>
        <p className="text-sm text-text-muted max-w-2xl">
          Live view of the <span className="font-mono">agent_config</span>{" "}
          tab in the platform Sheet. Edit any row inline and click{" "}
          <span className="font-medium text-text">Save changes</span>{" "}
          to write to the Sheet. The Sheet remains the canonical store —
          edits land there directly. Defaults shown alongside are the
          bootstrap fallbacks used when a cell is missing.
        </p>
      </header>

      <SaveBar
        dirtyCount={dirty.size}
        isSaving={isSaving}
        onSave={() => {
          void onSave();
        }}
      />

      {groups?.map(({ section, rows }) => (
        <SettingsSection
          key={section.id}
          section={section}
          rows={rows}
          dirty={dirty}
          firedWarnings={firedWarnings}
          saveStatus={saveStatus}
          onChange={setRowValue}
          onRevert={clearRow}
        />
      ))}
    </div>
  );
}

function SaveBar({
  dirtyCount,
  isSaving,
  onSave,
}: {
  dirtyCount: number;
  isSaving: boolean;
  onSave: () => void;
}) {
  return (
    <div
      className={cn(
        "sticky top-0 z-10 flex items-center justify-between gap-3 rounded border border-border-light bg-bg-card/95 px-4 py-3 backdrop-blur",
        dirtyCount === 0 && "opacity-80",
      )}
    >
      <p className="text-sm text-text-muted">
        {dirtyCount === 0
          ? "No pending edits."
          : `${dirtyCount} pending edit${dirtyCount === 1 ? "" : "s"}.`}
      </p>
      <Button
        type="button"
        size="sm"
        onClick={onSave}
        disabled={dirtyCount === 0 || isSaving}
      >
        {isSaving ? "Saving…" : `Save changes${dirtyCount > 0 ? ` (${dirtyCount})` : ""}`}
      </Button>
    </div>
  );
}

interface SectionProps {
  section: Section;
  rows: AgentConfigRow[];
  dirty: DirtyMap;
  firedWarnings: ReadonlyMap<string, string>;
  saveStatus: SaveStatusMap;
  onChange: (key: string, value: string | number) => void;
  onRevert: (key: string) => void;
}

function SettingsSection({
  section,
  rows,
  dirty,
  firedWarnings,
  saveStatus,
  onChange,
  onRevert,
}: SectionProps) {
  const titleId = `settings-section-${section.id}`;
  return (
    <Card role="region" aria-labelledby={titleId} className="p-0">
      <details open className="group">
        <summary
          className={cn(
            "list-none cursor-pointer p-6 flex items-start gap-4",
            "[&::-webkit-details-marker]:hidden",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded",
          )}
        >
          <ChevronRight
            aria-hidden
            className="h-5 w-5 mt-0.5 text-text-muted transition-transform group-open:rotate-90 shrink-0"
          />
          <div className="flex-1 space-y-1">
            <h3 id={titleId} className="font-display text-xl text-text">
              {section.title}
            </h3>
            <p className="text-sm text-text-muted">{section.description}</p>
          </div>
        </summary>
        <div className="px-6 pb-6">
          {rows.length === 0 ? (
            <p className="text-sm text-text-faint italic">No keys in this section.</p>
          ) : (
            <ul className="space-y-5">
              {rows.map((row) => (
                <SettingsRow
                  key={row.key}
                  row={row}
                  pendingValue={dirty.get(row.key)}
                  warnings={warningsForKey(firedWarnings, row.key)}
                  status={saveStatus.get(row.key)}
                  onChange={onChange}
                  onRevert={onRevert}
                />
              ))}
            </ul>
          )}
        </div>
      </details>
    </Card>
  );
}

interface RowProps {
  row: AgentConfigRow;
  pendingValue: string | number | undefined;
  warnings: AgentConfigKeyWarning[];
  status: { status: SaveStatus; message?: string } | undefined;
  onChange: (key: string, value: string | number) => void;
  onRevert: (key: string) => void;
}

function SettingsRow({
  row,
  pendingValue,
  warnings,
  status,
  onChange,
  onRevert,
}: RowProps) {
  const isDirty = pendingValue !== undefined;
  const effective = isDirty ? pendingValue : row.value;
  return (
    <li className="grid gap-3 sm:grid-cols-[1fr_minmax(220px,360px)] sm:gap-x-8 sm:items-start">
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-text">{row.label}</span>
          <span className="font-mono text-[11px] text-text-faint">{row.key}</span>
          {isDirty ? (
            <span className="font-mono text-[10px] text-amber-400" aria-label="pending edit">
              ● modified
            </span>
          ) : null}
          {status?.status === "saved" ? (
            <span className="font-mono text-[10px] text-emerald-400">saved</span>
          ) : null}
          {status?.status === "failed" ? (
            <span
              className="font-mono text-[10px] text-red-300"
              title={status.message ?? "save failed"}
            >
              save failed
            </span>
          ) : null}
        </div>
        {row.description ? (
          <p className="text-xs text-text-muted">{row.description}</p>
        ) : null}
        {warnings.map((warning) => (
          <p
            key={warning.ruleId}
            role="status"
            className="text-[11px] text-amber-300 border-l-2 border-amber-400/40 pl-2"
          >
            {warning.message}
          </p>
        ))}
      </div>
      <div className="space-y-1">
        <ValueEditor
          row={row}
          value={effective}
          isDirty={isDirty}
          onChange={onChange}
          onRevert={onRevert}
        />
        <DefaultHint row={row} effective={effective} />
      </div>
    </li>
  );
}

function ValueEditor({
  row,
  value,
  isDirty,
  onChange,
  onRevert,
}: {
  row: AgentConfigRow;
  value: string | number | null | undefined;
  isDirty: boolean;
  onChange: (key: string, value: string | number) => void;
  onRevert: (key: string) => void;
}) {
  if (!row.isKnown) return <ReadOnlyValue row={row} />;

  const editor = (() => {
    switch (row.inputKind) {
      case "select":
        return (
          <ModelIdEditor
            row={row}
            value={value === null || value === undefined ? "" : String(value)}
            onChange={(next) => onChange(row.key, next)}
          />
        );
      case "email-list":
        return (
          <EmailTagEditor
            row={row}
            value={typeof value === "string" ? value : value === null || value === undefined ? "" : String(value)}
            onChange={(next) => onChange(row.key, next)}
          />
        );
      case "text":
      default:
        return (
          <TextValueEditor row={row} value={value} onChange={onChange} />
        );
    }
  })();

  return (
    <div className="space-y-1">
      {editor}
      {isDirty ? (
        <button
          type="button"
          className="text-[11px] text-text-muted hover:text-text underline-offset-2 hover:underline"
          onClick={() => onRevert(row.key)}
        >
          Revert
        </button>
      ) : null}
    </div>
  );
}

function ReadOnlyValue({ row }: { row: AgentConfigRow }) {
  if (row.value === null) {
    return <span className="font-mono text-text-faint">—</span>;
  }
  return <span className="font-mono text-accent">{String(row.value)}</span>;
}

/**
 * Plain text editor. Preserves number ↔ string typing by inspecting
 * the row's default (which is always present for known keys): a
 * numeric default round-trips as a number; everything else as a
 * string. This way an operator typing `42` in a numeric field doesn't
 * silently flip it to `"42"` in the Sheet.
 *
 * The number-detection is deliberately strict (`raw.trim() === String(n)`).
 * It rejects formatting variants like `"1.0"` (`Number("1.0") === 1`,
 * `String(1) === "1"` → mismatch → falls through as a string) and
 * leading-zero / mid-edit sequences like `"01"` or `"1."`. Those land
 * as strings in the Sheet rather than the operator's literal numeric
 * form being silently rewritten on Save. The agent-boot Zod is the
 * loud-fail backstop on garbage values, so this is intentionally
 * lenient — the goal is "don't surprise the operator," not "guarantee
 * canonical numeric form."
 */
function TextValueEditor({
  row,
  value,
  onChange,
}: {
  row: AgentConfigRow;
  value: string | number | null | undefined;
  onChange: (key: string, value: string | number) => void;
}) {
  const display = value === null || value === undefined ? "" : String(value);
  const isNumeric = typeof row.default === "number";
  return (
    <input
      type="text"
      inputMode={isNumeric ? "decimal" : "text"}
      value={display}
      onChange={(e) => {
        const raw = e.target.value;
        if (!isNumeric) {
          onChange(row.key, raw);
          return;
        }
        if (raw === "") {
          onChange(row.key, "");
          return;
        }
        const n = Number(raw);
        if (Number.isFinite(n) && raw.trim() === String(n)) {
          onChange(row.key, n);
        } else {
          onChange(row.key, raw);
        }
      }}
      className={cn(
        "w-full rounded border border-border-light bg-bg-card px-3 py-1.5 text-sm font-mono text-text",
        "placeholder:text-text-muted/60",
        "focus:outline-none focus:ring-2 focus:ring-accent",
      )}
    />
  );
}

function ModelIdEditor({
  row,
  value,
  onChange,
}: {
  row: AgentConfigRow;
  value: string;
  onChange: (next: string) => void;
}) {
  const options = row.options ?? [];
  // Resolve friendly model labels for both the dropdown items and
  // the collapsed trigger. Lookup miss falls back to the raw id —
  // covers the brief window before the registry query resolves and
  // the rare case where the live Sheet carries a model id absent
  // from `KNOWN_MODEL_DISPLAY_NAMES` (e.g. an Anthropic model rolled
  // out before its display-name entry shipped). The raw id stays
  // visible alongside the friendly name on every surface — operators
  // need it to type into the Sheet directly when editing a row that
  // isn't surfaced in this dropdown's option list.
  const { modelsById } = useModels();
  const selectedDisplay = value ? (modelsById[value]?.displayName ?? value) : null;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={row.label}>
        {selectedDisplay !== null ? (
          <span className="flex items-baseline gap-2 truncate">
            <span className="text-sm">{selectedDisplay}</span>
            <span className="font-mono text-[11px] text-text-muted truncate">
              {value}
            </span>
          </span>
        ) : (
          <SelectValue placeholder="(select a model)" />
        )}
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>
            <div className="flex flex-col">
              <span className="text-sm">
                {modelsById[opt]?.displayName ?? opt}
              </span>
              <span className="font-mono text-[11px] text-text-muted">
                {opt}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function EmailTagEditor({
  row,
  value,
  onChange,
}: {
  row: AgentConfigRow;
  value: string;
  onChange: (next: string) => void;
}) {
  const emails = useMemo(() => parseEmailList(value), [value]);
  const [draft, setDraft] = useState("");

  const commitDraft = useCallback(
    (raw: string) => {
      const additions = splitEmailString(raw);
      if (additions.length === 0) {
        setDraft("");
        return;
      }
      const next = dedupeEmails([...emails, ...additions]);
      onChange(serializeEmailList(next));
      setDraft("");
    },
    [emails, onChange],
  );

  const removeAt = useCallback(
    (index: number) => {
      const next = emails.filter((_, i) => i !== index);
      onChange(serializeEmailList(next));
    },
    [emails, onChange],
  );

  const draftIsValid = draft.length === 0 || isValidEmail(draft.trim());

  return (
    <div className="space-y-1">
      <div
        className={cn(
          "flex flex-wrap gap-1 rounded border border-border-light bg-bg-card px-2 py-1.5",
          "focus-within:ring-2 focus-within:ring-accent",
        )}
      >
        {emails.map((email, i) => {
          const valid = isValidEmail(email);
          return (
            <span
              key={`${email}-${i}`}
              className={cn(
                "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-mono",
                valid
                  ? "bg-bg-card-hover text-text"
                  : "bg-red-500/10 text-red-300 border border-red-500/30",
              )}
              title={valid ? undefined : "not a valid email"}
            >
              {email}
              <button
                type="button"
                aria-label={`Remove ${email}`}
                className="text-text-muted hover:text-text"
                onClick={() => removeAt(i)}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commitDraft(draft);
            } else if (e.key === "Backspace" && draft.length === 0 && emails.length > 0) {
              removeAt(emails.length - 1);
            }
          }}
          onBlur={() => {
            if (draft.length > 0) commitDraft(draft);
          }}
          placeholder={emails.length === 0 ? "ops@…" : ""}
          aria-label={`${row.label} — add email`}
          className="flex-1 min-w-[8rem] bg-transparent text-sm font-mono text-text placeholder:text-text-muted/60 focus:outline-none"
        />
      </div>
      {!draftIsValid ? (
        <p className="text-[11px] text-red-300">Enter a valid email address.</p>
      ) : null}
    </div>
  );
}

/**
 * "default: X" hint when the live value diverges from the bootstrap
 * default. Reflects the *effective* value (live + pending edits) so
 * an operator typing the default back into a row sees the hint
 * disappear in real time. Hidden when the values match or when the
 * row is unknown (no default exists).
 */
function DefaultHint({
  row,
  effective,
}: {
  row: AgentConfigRow;
  effective: string | number | null | undefined;
}) {
  if (row.default === null) return null;
  if (effective === null || effective === undefined) {
    // Only show the hint while the cell is genuinely missing — not
    // while the operator is mid-edit with an empty input.
    if (row.value === null) return null;
  }
  if (String(effective) === String(row.default)) return null;
  return (
    <p className="text-[11px] text-text-faint mt-0.5">
      default: <span className="font-mono">{String(row.default)}</span>
    </p>
  );
}
